import {
    DefaultFooterComponent,
    Dropdown,
    Icon,
    Icons,
    Label,
    MultilineInput,
    Popup,
    Radio,
    RadioGroup,
    TextInput
} from '@silverfort/sf-components';
import { FC, useCallback, useState } from 'react';
import { CLASSIFICATION_OPTIONS } from '../../ServiceAccountsConsts';

interface PopupProps {
    open: boolean;
    onClose: () => void;
    onApprove: (val?: string) => void;
}

interface RemoveAccountPopupProps {
    title: string;
    isProtected?: boolean;
    open: boolean;
    onClose: () => void;
    onApprove: (reason?: string, otherReason?: string, furtherDetails?: string) => void;
    reasonOptions: {
        title: string;
        value: string;
    }[];
    descriptionMessage?: string;
}

interface RemoveAccountContentProps {
    text: string;
    reasonOptions: { title: string; value: string }[];
    isProtected?: boolean;
    furtherDetails: string | undefined;
    setFurtherDetails: (val: string) => void;
    otherReason: string | undefined;
    setOtherReason: (val: string) => void;
    reason: string | number | undefined;
    setReason: (val: string | number) => void;
}

const RemoveAccountContent: FC<RemoveAccountContentProps> = ({
    text,
    isProtected,
    reasonOptions,
    furtherDetails,
    setFurtherDetails,
    otherReason,
    setOtherReason,
    reason,
    setReason
}) => {
    return (
        <div className="text-sf-gray-800 text-sf text-13 w-[560px] py-2">
            <div className="flex flex-col justify-between h-18">
                {text.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                ))}
            </div>
            <RadioGroup
                orientation={'vertical'}
                options={reasonOptions}
                selectedOption={reason || ''}
                onChange={(option) => {
                    setOtherReason('');
                    setReason(option);
                }}
                itemContainerClassName="border border-sf-gray-100 rounded-lg p-4 mt-4 w-full"
            />
            <div className="border border-sf-gray-100 rounded-lg p-4 mt-4 w-full">
                <Radio
                    containerClassName=""
                    title="Other"
                    checked={reason === 'other'}
                    value={'other'}
                    onChange={(event) => setReason(event.target.value)}></Radio>
                <TextInput
                    hidden={reason !== 'other'}
                    className="w-full mt-2"
                    value={otherReason}
                    onChange={(text) => {
                        setOtherReason(text);
                    }}
                />
            </div>

            <div className="mt-4">
                <Label text="Notes" className="text-sf-gray-800" />
                <MultilineInput
                    placeholder="Add more details here."
                    className="w-full h-20 mt-1 flex items-start "
                    value={furtherDetails}
                    onChange={(text) => {
                        setFurtherDetails(text);
                    }}
                    multiline
                />
            </div>
            {isProtected && (
                <>
                    <div className="flex items-center gap-1 text-10 text-sf-neutral-500 pt-6">
                        <Icon icon={Icons.Warning} size={12} className="sf-neutral-500" />
                        Active Policy
                    </div>
                    <div>Discarding this account will disable an active policy.</div>
                </>
            )}
        </div>
    );
};

export const DiscardAccountPopup: FC<RemoveAccountPopupProps> = ({
    open,
    onClose,
    onApprove,
    title,
    isProtected,
    reasonOptions,
    descriptionMessage
}) => {
    const [reason, setReason] = useState<string | number | undefined>(undefined);
    const [otherReason, setOtherReason] = useState<string | undefined>(undefined);
    const [furtherDetails, setFurtherDetails] = useState<string | undefined>(undefined);
    return (
        <Popup
            className="z-[7000]"
            popupClassName="w-[560px] h-[554px]"
            open={open}
            title={title}
            ContentComponent={RemoveAccountContent}
            ContentComponentProps={{
                text: descriptionMessage || '',
                isProtected,
                reasonOptions,
                furtherDetails,
                setFurtherDetails,
                otherReason,
                setOtherReason,
                reason,
                setReason
            }}
            FooterComponent={DefaultFooterComponent}
            FooterComponentProps={{ primaryButtonProps: { text: 'Save' }, secondaryButtonProps: { text: 'Cancel' } }}
            onClose={onClose}
            onApprove={() => {
                onApprove(reason as string, otherReason, furtherDetails);
            }}
        />
    );
};

const ChangeAccountCategoryContent = ({ setSelectedType, selectedType }) => {
    return (
        <Dropdown
            className="w-full"
            variant={'trigger'}
            selectedItems={selectedType}
            items={CLASSIFICATION_OPTIONS}
            onChange={setSelectedType}
            fullMenuWidth
        />
    );
};

export const ChangeAccountCategoryPopup: FC<PopupProps> = ({ open, onClose, onApprove }) => {
    const [selectedType, setSelectedType] = useState<string | undefined>(undefined);

    const onSave = useCallback(() => {
        onApprove(selectedType);
        onClose();
    }, [onApprove, onClose, selectedType]);

    return (
        <Popup
            className="z-[7000]"
            popupClassName="w-[360px] h-[200px]"
            open={open}
            title={'Change Account Category'}
            ContentComponent={ChangeAccountCategoryContent}
            ContentComponentProps={{ setSelectedType, selectedType }}
            FooterComponent={DefaultFooterComponent}
            onApprove={onSave}
            onClose={onClose}
        />
    );
};
